// Food and drink order pricing: a menu line's price, its tax, and an order's totals (F-FNB-01, F-POS-01).
//
// Spec: spec/13-venue-console.md §6.1 (products, variants, modifiers), spec/06 §8.1 (order lines),
// spec/06 §10 (tax modes and the tax engine).
//
// This is the F&B slice of the order maths, and it does no new arithmetic: a line's amount is the
// base unit price plus its chosen modifiers, times the quantity — all through `packages/domain`'s
// money primitives (`sumMoney`, `multiplyMoney`) — and the tax on it is the shared tax engine
// (`computeLineTaxComponents`, spec/06 §10.1), decided once and split into its GST components. So the
// server, the console's price preview and the kiosk cart all compute the same paisa, and a line's
// tax always adds back to the price the player was shown (spec/06 §1.4).
//
// A **treatment** carries the venue's tax mode: a `regular` venue charges tax (inclusive or exclusive
// per `tax.pricesInclusive`), a `composition` or `unregistered` venue charges none and the line's
// value is simply its price (spec/06 §10.3). The order's treatment is fixed when it is placed and
// stored on it, so a later change of mode never re-prices it (spec/06 §10.3 rule 3).
import {} from '@clavian/contracts';
import { multiplyMoney, sumMoney, zeroMoney } from "../money/index.js";
import { computeLineTaxComponents, } from "../tax/index.js";
/**
 * The price and tax of one food order line.
 *
 * The line amount is the unit price plus its modifiers, times the quantity — exact, nothing rounded
 * until the tax (spec/06 §1.4). At a venue that charges no tax the amount is the whole value and the
 * tax is zero; otherwise the shared tax engine backs the tax out of (inclusive) or adds it to
 * (exclusive) the amount and splits it into components.
 */
export function priceFoodOrderLine(input, treatment) {
    const currency = input.unitPrice.currency;
    const unitGross = sumMoney([input.unitPrice, ...input.modifierPrices], currency);
    const lineAmount = multiplyMoney(unitGross, input.quantity);
    if (!treatment.charges) {
        return { unitGross, taxable: lineAmount, tax: zeroMoney(currency), gross: lineAmount, components: [] };
    }
    const taxed = computeLineTaxComponents(lineAmount, input.taxRateBps, treatment.pricing, treatment.place);
    return {
        unitGross,
        taxable: taxed.taxableAmount,
        tax: taxed.taxAmount,
        gross: taxed.grossAmount,
        components: taxed.components,
    };
}
/**
 * The price of a whole food order: every line priced, and the order's taxable subtotal, tax and
 * total summed from them. `total = subtotal + tax` by construction — each line's gross is its
 * taxable plus its tax — so the order's total always adds back to what the player pays (spec/06 §1.4).
 * `currency` is required so an empty order still has one to sum in (spec/06 §1.2).
 */
export function priceFoodOrder(inputs, treatment, currency) {
    const lines = inputs.map((input) => priceFoodOrderLine(input, treatment));
    return {
        subtotal: sumMoney(lines.map((line) => line.taxable), currency),
        tax: sumMoney(lines.map((line) => line.tax), currency),
        total: sumMoney(lines.map((line) => line.gross), currency),
        lines,
    };
}
export * from "./menu-rules.js";
//# sourceMappingURL=index.js.map